import { useState, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { parsePrice } from "../utils/format";
import { rowKey } from "../utils/catalogGrouping";
import { toastOptions } from "../utils/toastOptions";

const PATH_TO_BACKEND = process.env.REACT_APP_PATH_TO_BACKEND;

// Корзина + отправка в сделку через тот же GET->merge->SET эндпоинт /api/sender
// (routes/senderToBitrix.js), который уже используется при переносе товаров из калькулятора.
export const useCart = ({ priceBasis, dealId, getMeasureCode }) => {
    const [cart, setCart] = useState([]);
    const [rowQuantities, setRowQuantities] = useState({});
    const [submitting, setSubmitting] = useState(false);

    const handleQuantityChange = (row, value) => {
        const quantity = Math.max(1, Number(value) || 1);
        setRowQuantities((prev) => ({ ...prev, [rowKey(row)]: quantity }));
    };

    const handleAddRowToCart = (row) => {
        const quantity = rowQuantities[rowKey(row)] || 1;
        const key = rowKey(row);
        setCart((prev) => {
            const existing = prev.find((item) => item.key === key);
            if (existing) {
                return prev.map((item) => item.key === key ? { ...item, quantity: item.quantity + quantity } : item);
            }
            return [...prev, {
                key,
                bitrixId: row.bitrix_id,
                name: row.product_calculator_name,
                source: row.source,
                manufacturer: row.manufacturer,
                unit: row.unit,
                priceCash: parsePrice(row.price_cash),
                priceNonCash: parsePrice(row.price_non_cash),
                quantity,
            }];
        });
        toast.success(`Добавлено в корзину: ${row.product_calculator_name}`, toastOptions);
    };

    const updateCartQuantity = (key, value) => {
        const quantity = Math.max(1, Number(value) || 1);
        setCart((prev) => prev.map((item) => item.key === key ? { ...item, quantity } : item));
    };

    const removeFromCart = (key) => {
        setCart((prev) => prev.filter((item) => item.key !== key));
    };

    const cartItemPrice = useCallback(
        (item) => (priceBasis === "cash" ? item.priceCash : item.priceNonCash) || 0,
        [priceBasis]
    );
    const cartTotal = useMemo(
        () => cart.reduce((sum, item) => sum + cartItemPrice(item) * item.quantity, 0),
        [cart, cartItemPrice]
    );
    const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

    const handleAddToDeal = async () => {
        if (!dealId) {
            toast.error("Добавление в сделку доступно только внутри карточки сделки Bitrix24", toastOptions);
            return;
        }
        if (cart.length === 0) {
            toast.info("Корзина пуста", toastOptions);
            return;
        }

        setSubmitting(true);
        try {
            const products = cart.map((item) => ({
                bitrixId: item.bitrixId,
                price: cartItemPrice(item),
                quantity: item.quantity,
                measureCode: getMeasureCode(item.unit),
            }));

            const response = await axios.post(`${PATH_TO_BACKEND}/api/sender`, { dealId, products });
            if (response.data.success) {
                toast.success(`Добавлено в сделку: ${response.data.bitrixProducts} товаров`, toastOptions);
                setCart([]);
            } else {
                throw new Error(response.data.message || "Не удалось добавить товары в сделку");
            }
        } catch (error) {
            console.error("Ошибка при добавлении товаров в сделку:", error);
            toast.error(`Ошибка при добавлении в сделку: ${error.message}`, toastOptions);
        } finally {
            setSubmitting(false);
        }
    };

    return {
        cart,
        rowQuantities,
        submitting,
        handleQuantityChange,
        handleAddRowToCart,
        updateCartQuantity,
        removeFromCart,
        cartItemPrice,
        cartTotal,
        cartCount,
        handleAddToDeal,
    };
};
